import { ValidationError, type AnySchema } from "yup";
import type { TeacherVM } from "@shared-kernel/application/ports/teacher/teacher-repository";
import { formatTeacherDataToFormData, schema as professionalSchema } from "./form-validation/teacher-profile.schema";
import {
  formatTeacherDataToPedagogyFormData,
  teacherPedagogySchema,
} from "@user-management-context/primary/teacher/teacher-pedagogy-subform/teacher-pedagogy.subform.schema";
import {
  formatTeacherDataToPublicProfileFormData,
  teacherPublicProfileSchema,
} from "@user-management-context/primary/teacher/teacher-public-profile-subform/teacher-public-profile.subform.schema";

export interface TeacherProfileCompleteness {
  professionalMissingCount: number;
  pedagogyMissingCount: number;
  publicProfileMissingCount: number;
  totalMissingCount: number;
}

const countMissingFields = (schema: AnySchema, value: unknown): number => {
  try {
    schema.validateSync(value, { abortEarly: false });
    return 0;
  } catch (error) {
    if (error instanceof ValidationError) {
      // Collapse nested paths to their top-level field so a single missing object
      // (e.g. postalAddress producing errors on both placeId and description) counts as one.
      const paths = new Set(error.inner.map(e => e.path?.split(".")[0]).filter((p): p is string => Boolean(p)));
      return paths.size || error.inner.length || 1;
    }
    return 1;
  }
};

export const getTeacherProfileCompleteness = (teacher: TeacherVM): TeacherProfileCompleteness => {
  const professionalMissingCount = countMissingFields(professionalSchema, formatTeacherDataToFormData(teacher));
  const pedagogyMissingCount = countMissingFields(teacherPedagogySchema, formatTeacherDataToPedagogyFormData(teacher));
  const publicProfileMissingCount = countMissingFields(teacherPublicProfileSchema, formatTeacherDataToPublicProfileFormData(teacher));
  return {
    professionalMissingCount,
    pedagogyMissingCount,
    publicProfileMissingCount,
    totalMissingCount: professionalMissingCount + pedagogyMissingCount + publicProfileMissingCount,
  };
};
